import { HeroSection } from '../components/HeroSection';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import { DEFAULT_SERVICES } from '../data/defaults';
import { orderHeroStats } from '../utils/heroStats';
import { onlineServicesHref } from '../utils/links';
import varlik1 from '../components/assets/icon-recycle.png';
import varlik2 from '../components/assets/Varlık 2.png';
import varlik31 from '../components/assets/Varlık 31.png';
import varlik32 from '../components/assets/Varlık 32.png';
import varlik93 from '../components/assets/Varlık 93.png';
import serviceImageA from '../components/assets/2.jpg';
import serviceImageB from '../components/assets/11.jpg';
import serviceImageC from '../components/assets/522.png';
import { ContentBlock } from '../types/content';

const PAGE_SLUG = 'hizmetler';

const DEFAULT_OPS = {
  eyebrow: 'OPERASYONLAR',
  title: 'Çok Amaçlı Liman Operasyonları',
  description:
    "Konteyner, genel kargo ve proje yüklerinde 7/24 kesintisiz operasyon anlayışıyla, Doğu Akdeniz'in en güvenilir liman işletmelerinden biriyiz.",
  ctaText: 'Daha Fazla Bilgi',
  ctaLink: '/liman-ozellikleri',
};

const OPERATIONAL_METRICS = [
  { value: '5', label: 'MHC' },
  { value: '4', label: 'RS' },
  { value: '1', label: 'ECH' },
  { value: '10', label: 'TT' },
];

const SERVICE_ICONS: { match: string; icon: string }[] = [
  { match: 'konteyner', icon: varlik32 },
  { match: 'dokme', icon: varlik31 },
  { match: 'genel kargo', icon: varlik31 },
  { match: 'proje', icon: varlik2 },
  { match: 'antrepo', icon: varlik93 },
  { match: 'depo', icon: varlik93 },
  { match: 'silo', icon: varlik93 },
  { match: 'atik', icon: varlik1 },
];

const SERVICE_PHOTOS: { match: string; image: string }[] = [
  { match: 'konteyner', image: serviceImageB },
  { match: 'dokme', image: serviceImageA },
  { match: 'genel kargo', image: serviceImageA },
  { match: 'cfs', image: serviceImageA },
  { match: 'proje', image: serviceImageC },
  { match: 'antrepo', image: serviceImageC },
  { match: 'depo', image: serviceImageC },
  { match: 'silo', image: serviceImageC },
  { match: 'atik', image: serviceImageB },
];

function normalizeTitle(title: string) {
  return title
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g');
}

function serviceIcon(title: string) {
  const normalized = normalizeTitle(title);
  return SERVICE_ICONS.find((item) => normalized.includes(item.match))?.icon || varlik32;
}

function serviceFallbackPhoto(title: string) {
  const normalized = normalizeTitle(title);
  const matched = SERVICE_PHOTOS.find((item) => normalized.includes(item.match))?.image;
  if (matched) return matched;
  const fromDefaults = DEFAULT_SERVICES.find((item) =>
    normalizeTitle(item.title).includes(normalized.split(' ')[0] || ''),
  )?.imageUrl;
  return fromDefaults || serviceImageA;
}

type ServiceShowcase = {
  key: string;
  order: number;
  eyebrow?: string;
  title: string;
  description?: string;
  imageUrl?: string;
  features: string[];
};

type OnlineServiceCard = {
  key: string;
  title?: string;
  description?: string;
  badge?: string;
  footer?: string;
  buttonText?: string;
  buttonLink?: string;
  iconUrl?: string;
  features: string[];
};

// Portal maddeleri kaydederken gercek satir sonu da duz "\n" metni de yazabiliyor.
function parseServiceFeatures(value?: string) {
  return (value || '')
    .split(/\\n|\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

// Hizmet basliginda ilk kelime kirmizi, kalani lacivert gosterilir.
function formatServiceTitle(value?: string) {
  const text = (value || '').trim();
  if (!text || text.includes('<')) return text;

  const [first, ...rest] = text.split(/\s+/);
  if (rest.length === 0) return text;
  return `<span>${first}</span> ${rest.join(' ')}`;
}

function splitLastWord(value?: string) {
  const text = (value || '').trim();
  if (!text) return { lead: '', accent: '' };
  const parts = text.split(/\s+/);
  if (parts.length === 1) return { lead: parts[0], accent: '' };
  return {
    lead: parts.slice(0, -1).join(' '),
    accent: parts[parts.length - 1],
  };
}

type ShowcaseField = 'eyebrow' | 'title' | 'description' | 'image' | 'features';

// Alan adi hem Turkce ("Ust Etiket") hem portal anahtari ("eyebrow") olarak gelebilir.
function showcaseField(raw: string): ShowcaseField | undefined {
  const key = normalizeTitle(raw).replace(/[_\s]+/g, '-');
  if (key.includes('ust-etiket') || key.includes('eyebrow')) return 'eyebrow';
  if (key.includes('baslik') || key.includes('title')) return 'title';
  if (key.includes('aciklama') || key.includes('description')) return 'description';
  if (key.includes('gorsel') || key.includes('image')) return 'image';
  if (key.includes('ozellik') || key.includes('features')) return 'features';
  return undefined;
}

/** Hizmet kartlari uc bicimde kaydedilebiliyor: "01-hizmet-<slug>-<alan>",
 *  portalin "services_card_<slug>_<alan>" anahtari veya "01 HIZMET - X - Alan" basligi. */
function matchShowcaseBlock(block: ContentBlock) {
  const blockKey = block.blockKey || '';

  const numbered = blockKey.match(
    /^(\d+)-hizmet-(.+?)-(ust-etiket|baslik|aciklama|gorsel-url|ozellikler)$/,
  );
  if (numbered) {
    return {
      serviceKey: `${numbered[1]}-${numbered[2]}`,
      order: Number(numbered[1]),
      field: showcaseField(numbered[3]),
    };
  }

  const card = blockKey.match(/^services_card_(.+?)_([a-z_]+)$/);
  if (card) {
    return {
      serviceKey: card[1],
      order: block.sortOrder ?? 0,
      field: showcaseField(card[2]),
    };
  }

  const fromTitle = normalizeTitle(block.title || '').match(
    /^(\d+)\s*hizmet\s*-\s*(.+?)\s*-\s*(ust etiket|baslik|aciklama|gorsel url|ozellikler)$/,
  );
  if (fromTitle) {
    return {
      serviceKey: `${fromTitle[1]}-${fromTitle[2].replace(/\s+/g, '-')}`,
      order: Number(fromTitle[1]),
      field: showcaseField(fromTitle[3]),
    };
  }

  return undefined;
}

function buildServiceShowcases(blocks: ContentBlock[]): ServiceShowcase[] {
  const map = new Map<string, ServiceShowcase>();

  blocks.forEach((block) => {
    if (block.pageSlug !== PAGE_SLUG) return;
    const matched = matchShowcaseBlock(block);
    if (!matched || !matched.field) return;

    const { serviceKey, order, field } = matched;
    const current = map.get(serviceKey) || {
      key: serviceKey,
      order,
      title: '',
      features: [],
    };
    current.order = Math.min(current.order, order);

    if (field === 'eyebrow') current.eyebrow = block.content || block.title;
    if (field === 'title') current.title = block.content || current.title;
    if (field === 'description') current.description = block.content || current.description;
    if (field === 'image') current.imageUrl = block.content || block.imageUrl || current.imageUrl;
    if (field === 'features') current.features = parseServiceFeatures(block.content);

    map.set(serviceKey, current);
  });

  return Array.from(map.values())
    .filter((item) => item.title)
    .sort((a, b) => a.order - b.order);
}

function buildOnlineServiceCards(blocks: ContentBlock[]) {
  const intro = {
    eyebrow: '',
    title: '',
    description: '',
  };
  const cards = new Map<string, OnlineServiceCard>();

  blocks.forEach((block) => {
    if (block.pageSlug !== PAGE_SLUG) return;
    const blockKey = block.blockKey || '';
    const normalizedKey = normalizeTitle(blockKey);
    const normalizedTitle = normalizeTitle(block.title || '');
    // Portal anahtarli bloklarda bos alan bos kalir; baslik ("Ikon URL" gibi) deger sayilmaz
    const value = block.content || (blockKey.startsWith('services_online_') ? '' : block.title) || '';

    if (
      blockKey === 'services_online_eyebrow' ||
      normalizedTitle.includes('online hizmetler - ust etiket')
    ) {
      intro.eyebrow = value;
    }
    if (
      blockKey === 'services_online_title' ||
      normalizedTitle.includes('online hizmetler - baslik')
    ) {
      intro.title = value;
    }
    if (
      blockKey === 'services_online_description' ||
      normalizedTitle.includes('online hizmetler - aciklama')
    ) {
      intro.description = value;
    }

    const cardMatch =
      blockKey.match(/^services_online_card_(\d+)_(icon[-_]url|button-link|cta_link|features|title|button-text|cta_text|footer[-_]label|badge|description)$/) ||
      normalizedKey.match(/^services-online-card-(\d+)-(.+)$/) ||
      normalizedTitle.match(/online kart (\d+) - (.+)$/);
    if (!cardMatch) return;

    const cardKey = cardMatch[1];
    const field = cardMatch[2];
    const current = cards.get(cardKey) || { key: cardKey, features: [] };

    if (field.includes('title') || field.includes('baslik')) current.title = value;
    if (field.includes('description') || field.includes('aciklama')) current.description = value;
    if (field.includes('badge') || field.includes('rozet')) current.badge = value;
    // Portal anahtarlari: footer_label, cta_text, cta_link, icon_url
    if (/footer[-_]label|alt etiket/.test(field)) current.footer = value;
    if (/button-text|cta_text|buton metni/.test(field)) current.buttonText = value;
    if (/button-link|cta_link|buton linki/.test(field)) current.buttonLink = value;
    if (/icon[-_]url|ikon url/.test(field)) current.iconUrl = value;
    if (field.includes('features') || field.includes('ozellikler')) {
      current.features = parseServiceFeatures(value);
    }

    cards.set(cardKey, current);
  });

  // Yalnizca Terminal Isletim Sistemi (kart 1) gosterilir; Musteri Portali karti kaldirildi.
  return {
    intro,
    cards: Array.from(cards.values()).filter((card) => card.key === '1'),
  };
}

export function ServicesPage() {
  const { getPage, getBlock, getStats, content } = useSiteContent();
  const { t, tList } = useLanguage();
  const page = getPage(PAGE_SLUG);
  // 250K TEU | 8M TON | 1.3M m² (sira birime gore sabit; utils/heroStats)
  const heroStats = orderHeroStats(getStats('hero'));
  const services = content.services.filter((item) => item.isPublished !== false);
  // Portalda "Yayinda" isareti kaldirilan hizmetin karti (ayni slug) sayfada gosterilmez
  const hiddenServiceKeys = new Set(
    content.services.filter((item) => item.isPublished === false).map((item) => item.slug),
  );
  const serviceShowcases = buildServiceShowcases(content.contentBlocks).filter(
    (item) => !hiddenServiceKeys.has(item.key),
  );
  const onlineServices = buildOnlineServiceCards(content.contentBlocks);
  // "TOS'a Giris" butonu, ust menudeki kirmizi "Online Hizmetler" butonuyla ayni adrese gider
  // (portal Site Ayarlari > Online Hizmetler URL; bossa https://online.tbhport.com). Kartin
  // "Buton Linki" alani kullanilmaz, adres tek yerden yonetilir.
  const onlineServicesUrl = onlineServicesHref(content.settings.onlineServicesUrl);
  const quickLinks =
    content.settings.quickLinks && content.settings.quickLinks.length > 0
      ? content.settings.quickLinks
      : [
          { label: 'Konteyner', path: '/hizmetler' },
          { label: 'Genel Kargo / Dökme Yük', path: '/hizmetler' },
          { label: 'Proje Kargo', path: '/hizmetler' },
          { label: 'Antrepo, Depo ve Silo', path: '/hizmetler' },
          { label: 'Atık Alım', path: '/hizmetler' },
        ];
  const findServicesBlock = (...patterns: string[]) =>
    content.contentBlocks.find((block) => {
      if (block.pageSlug !== PAGE_SLUG) return false;
      const haystack = normalizeTitle(`${block.title || ''} ${block.content || ''} ${block.blockKey || ''}`);
      return patterns.some((pattern) => haystack.includes(normalizeTitle(pattern)));
    });

  const heroEyebrow =
    getBlock(PAGE_SLUG, 'services_hero_eyebrow') ||
    findServicesBlock('Hizmetler Hero - Ust Yazi', 'Hizmetler Hero - Ust Etiket', 'Ust Yazi', 'Ust Etiket');
  const heroTitle =
    getBlock(PAGE_SLUG, 'services_hero_title') ||
    findServicesBlock('Hizmetler Hero - Ana Baslik', 'Hizmetler Hero - Baslik', 'Ana Baslik', 'Baslik');
  const heroDescription =
    getBlock(PAGE_SLUG, 'services_hero_description') ||
    findServicesBlock('Hizmetler Hero - Aciklama', 'Aciklama');
  const heroImageBlock =
    getBlock(PAGE_SLUG, 'services_hero_image') ||
    findServicesBlock('Hizmetler Hero - Gorsel URL', 'Gorsel URL', '/api/dokumanlar/');
  // Banner gorseli: once icerik blogu, sonra sayfa kaydi; kullanilabilir adres
  // yoksa (ornegin portalda sadece dosya adi varsa) paketlenmis gorsele dusulur.
  const heroImage =
    mediaUrl(heroImageBlock?.content || heroImageBlock?.imageUrl) ||
    mediaUrl(page?.heroImage) ||
    serviceImageB;
  const heroCtaText =
    getBlock(PAGE_SLUG, 'services_hero_cta_text') ||
    findServicesBlock('Hizmetler Hero - Buton Metni', 'Buton Metni');
  const heroCtaLink =
    getBlock(PAGE_SLUG, 'services_hero_cta_link') ||
    findServicesBlock('Hizmetler Hero - Buton Linki', 'Buton Linki');

  return (
    <>
      {/* HeroSection metinleri kendi icinde cevrilir */}
      <HeroSection
        eyebrow={heroEyebrow?.content || heroEyebrow?.title || 'Hizmetler'}
        title={
          heroTitle?.content ||
          heroTitle?.title ||
          page?.heroTitle ||
          'Çok Amaçlı <em>Liman</em> Operasyonları'
        }
        subtitle={
          heroDescription?.content ||
          heroDescription?.title ||
          page?.heroSubtitle ||
          DEFAULT_OPS.description
        }
        imageUrl={heroImage}
        variant="services"
        stats={heroStats}
        ctaText={
          heroCtaText?.content ||
          heroCtaText?.title ||
          page?.heroCtaText ||
          DEFAULT_OPS.ctaText
        }
        ctaLink={
          heroCtaLink?.content ||
          heroCtaLink?.title ||
          page?.heroCtaLink ||
          DEFAULT_OPS.ctaLink
        }
      />

      <div className="quicklinks services-quicklinks container">
        {quickLinks.slice(0, 5).map((link) => (
          <a href={link.path} key={link.label}>
            <span className="quicklink-icon">
              <img src={serviceIcon(link.label)} alt="" />
            </span>
            <span className="quicklink-label">{t(link.label)}</span>
          </a>
        ))}
      </div>

      <section className="port-services-section">
        <div className="container">
          {(serviceShowcases.length > 0 ? serviceShowcases : services).map((service, index) => {
            const isShowcase = 'order' in service;
            const title = service.title;
            const image = isShowcase
              ? mediaUrl(service.imageUrl) || serviceFallbackPhoto(title)
              : mediaUrl(service.imageUrl) || serviceFallbackPhoto(title);
            const description = service.description;
            const features = service.features || [];
            // Katalog kaydinda kategori genelde "operasyon" sabiti; ust etiket
            // yerine sira numarasi gosterilir ("01 HIZMET").
            const serviceLabel = `${String(index + 1).padStart(2, '0')} HİZMET`;
            const eyebrow = isShowcase
              ? service.eyebrow || serviceLabel
              : normalizeTitle(service.category || '') === 'operasyon'
                ? serviceLabel
                : service.category || serviceLabel;
            const blockId = isShowcase ? service.key : service.slug || String(service.id);
            return (
              <div className="service-block" key={blockId} id={blockId}>
                <div className="img-col">
                  <img
                    src={image}
                    alt={t(title)}
                    className="img-fill"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = serviceFallbackPhoto(title);
                    }}
                  />
                </div>
                <div className="service-copy">
                  {eyebrow && <div className="eyebrow-sm">{t(eyebrow)}</div>}
                  <h3
                    className="service-block-title"
                    dangerouslySetInnerHTML={{ __html: formatServiceTitle(t(title)) }}
                  />
                  <p className="section-desc" style={{ marginBottom: 12 }}>
                    {t(description)}
                  </p>
                  {features.length > 0 && (
                    <ul className="check-list">
                      {tList(features).map((feature) => (
                        <li key={`check-${feature}`}>{feature}</li>
                      ))}
                    </ul>
                  )}
                  {!isShowcase && <DocumentLinks documents={service.documents} />}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="services-operational-strip">
        <div className="container services-operational-strip-inner">
          <h2 className="services-operational-strip-title">{t('Operasyonel etkinlik')}</h2>
          <div className="services-operational-strip-stats">
            {OPERATIONAL_METRICS.map((metric) => (
              <div key={metric.label}>
                <b>{metric.value}</b>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {onlineServices.cards.length > 0 && (
        <section className="services-online-section">
          <div className="container">
            <div className="services-online-eyebrow">
              {t(onlineServices.intro.eyebrow || 'ONLINE HIZMETLER')}
            </div>
            <h2 className="services-online-title">
              {(() => {
                const title = t(onlineServices.intro.title || 'Limanimiza dijital erisim');
                const [first = '', second = ''] = title.split(/ (.+)/);
                return (
                  <>
                    {first} <span>{second}</span>
                  </>
                );
              })()}
            </h2>
            <p className="services-online-description">
              {t(onlineServices.intro.description)}
            </p>

            <div className="services-online-grid">
              {onlineServices.cards.map((card) => (
                <article className="services-online-card" key={card.key}>
                  <div className="services-online-card-head">
                    <div className="services-online-card-icon">
                      {card.iconUrl ? <img src={mediaUrl(card.iconUrl) || card.iconUrl} alt="" /> : <span>◌</span>}
                    </div>
                    <div className="services-online-card-badge">
                      {t(card.badge || 'CANLI SISTEM')}
                    </div>
                  </div>

                  <h3 className="services-online-card-title">
                    {(() => {
                      const { lead, accent } = splitLastWord(t(card.title));
                      return (
                        <>
                          {lead} {accent && <span>{accent}</span>}
                        </>
                      );
                    })()}
                  </h3>

                  <p className="services-online-card-description">{t(card.description)}</p>

                  {card.features.length > 0 && (
                    <ul className="services-online-feature-list">
                      {tList(card.features).map((feature) => (
                        <li key={`${card.key}-${feature}`}>{feature}</li>
                      ))}
                    </ul>
                  )}

                  <div className="services-online-card-footer">
                    <span>{t(card.footer || 'Musteri erisimi')}</span>
                    <a href={onlineServicesUrl} target="_blank" rel="noreferrer" className="services-online-card-button">
                      {t(card.buttonText || 'Gir')}
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
